import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import {
  Building2,
  Search,
  Plus,
  RefreshCw,
  ArrowLeft,
  Eye,
  Pencil,
  Trash2,
  Wifi,
  WifiOff,
  Key,
  RotateCcw,
  Copy,
  Check,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Database,
  MapPin,
  Phone,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

export default function AdminEmpresas() {
  const navigate = useNavigate();
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });

  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [selectedEmpresa, setSelectedEmpresa] = useState(null);
  const [empresaDetail, setEmpresaDetail] = useState(null);
  const [copiedKey, setCopiedKey] = useState(false);

  // Estados para busca ERP
  const [showErpDialog, setShowErpDialog] = useState(false);
  const [erpClientes, setErpClientes] = useState([]);
  const [erpLoading, setErpLoading] = useState(false);
  const [erpSearch, setErpSearch] = useState("");
  const [erpError, setErpError] = useState("");
  const [erpFetching, setErpFetching] = useState(false); // Flag para evitar chamadas duplicadas

  const [formData, setFormData] = useState({
    cnpj: "",
    nome_empresa: "",
    numero_celular: "",
    status: "ativo",
    consentimento_whatsapp_habilitado: true,
  });
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState("");

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem("adminToken");
    return {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
  }, []);

  const fetchEmpresas = useCallback(
    async (showRefresh = false) => {
      if (showRefresh) setRefreshing(true);

      try {
        const params = new URLSearchParams({
          page: pagination.page.toString(),
          limit: pagination.limit.toString(),
        });
        if (search) params.append("search", search);
        if (statusFilter && statusFilter !== "todos")
          params.append("status", statusFilter);

        const response = await fetch(
          `${API_URL}/api/admin-panel/empresas?${params}`,
          {
            headers: getAuthHeaders(),
          },
        );

        if (response.status === 401) {
          localStorage.removeItem("adminToken");
          navigate("/admin");
          return;
        }

        const data = await response.json();
        if (data.success) {
          setEmpresas(data.data.empresas);
          setPagination((prev) => ({ ...prev, ...data.data.pagination }));
        }
      } catch (error) {
        console.error("Erro ao buscar empresas:", error);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [
      getAuthHeaders,
      navigate,
      pagination.page,
      pagination.limit,
      search,
      statusFilter,
    ],
  );

  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      navigate("/admin");
      return;
    }
    fetchEmpresas();
  }, [navigate, fetchEmpresas]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination((prev) => ({ ...prev, page: 1 }));
    fetchEmpresas();
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setFormError("");
    setFormLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/admin-panel/empresas`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Erro ao criar empresa");
      }

      setShowCreateDialog(false);
      setFormData({
        cnpj: "",
        nome_empresa: "",
        numero_celular: "",
        status: "ativo",
        consentimento_whatsapp_habilitado: true,
      });
      fetchEmpresas(true);
    } catch (error) {
      setFormError(error.message);
    } finally {
      setFormLoading(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setFormError("");
    setFormLoading(true);

    try {
      const response = await fetch(
        `${API_URL}/api/admin-panel/empresas/${selectedEmpresa.cnpj}`,
        {
          method: "PUT",
          headers: getAuthHeaders(),
          body: JSON.stringify({
            nome_empresa: formData.nome_empresa,
            numero_celular: formData.numero_celular,
            status: formData.status,
            consentimento_whatsapp_habilitado:
              formData.consentimento_whatsapp_habilitado,
          }),
        },
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Erro ao atualizar empresa");
      }

      setShowEditDialog(false);
      setSelectedEmpresa(null);
      fetchEmpresas(true);
    } catch (error) {
      setFormError(error.message);
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async () => {
    setFormLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/admin-panel/empresas/${selectedEmpresa.cnpj}`,
        {
          method: "DELETE",
          headers: getAuthHeaders(),
        },
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || "Erro ao deletar empresa");
      }

      setShowDeleteDialog(false);
      setSelectedEmpresa(null);
      fetchEmpresas(true);
    } catch (error) {
      alert(error.message);
    } finally {
      setFormLoading(false);
    }
  };

  const handleResetSession = async () => {
    setFormLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/admin-panel/empresas/${selectedEmpresa.cnpj}/reset-session`,
        {
          method: "POST",
          headers: getAuthHeaders(),
        },
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Erro ao resetar sessão");
      }

      setShowResetDialog(false);
      setSelectedEmpresa(null);
      fetchEmpresas(true);
      alert("Sessão resetada com sucesso!");
    } catch (error) {
      alert(error.message);
    } finally {
      setFormLoading(false);
    }
  };

  const handleRegenerateKey = async (cnpj) => {
    if (
      !window.confirm(
        "Tem certeza que deseja regenerar a API Key? A chave atual será invalidada.",
      )
    ) {
      return;
    }

    try {
      const response = await fetch(
        `${API_URL}/api/admin-panel/empresas/${cnpj}/regenerate-key`,
        {
          method: "POST",
          headers: getAuthHeaders(),
        },
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Erro ao regenerar API Key");
      }

      alert(
        `Nova API Key: ${data.data.api_key}\n\nCopie esta chave pois não será mostrada novamente.`,
      );

      if (empresaDetail && empresaDetail.empresa.cnpj === cnpj) {
        setEmpresaDetail((prev) => ({
          ...prev,
          empresa: { ...prev.empresa, api_key: data.data.api_key },
        }));
      }
    } catch (error) {
      alert(error.message);
    }
  };

  const fetchEmpresaDetail = async (cnpj) => {
    try {
      const response = await fetch(
        `${API_URL}/api/admin-panel/empresas/${cnpj}`,
        {
          headers: getAuthHeaders(),
        },
      );

      const data = await response.json();

      if (data.success) {
        setEmpresaDetail(data.data);
        setShowDetailDialog(true);
      }
    } catch (error) {
      console.error("Erro ao buscar detalhes:", error);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(true);
    setTimeout(() => setCopiedKey(false), 2000);
  };

  // Buscar clientes do ERP Wstech (usando axios para evitar problemas com fetch)
  const fetchErpClientes = async (searchTerm = "") => {
    if (erpFetching) return;

    setErpFetching(true);
    setErpLoading(true);
    setErpError("");

    try {
      const token = localStorage.getItem("adminToken");
      const params = searchTerm ? { search: searchTerm } : {};

      const response = await axios.get(
        `${API_URL}/api/admin-panel/erp/clientes`,
        {
          params,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.data?.success) {
        setErpClientes(response.data.data?.clientes || []);
      } else {
        throw new Error(response.data?.error || "Erro ao buscar clientes");
      }
    } catch (error) {
      console.error("Erro ao buscar ERP:", error);
      if (error.response?.status === 401) {
        localStorage.removeItem("adminToken");
        navigate("/admin");
        return;
      }
      setErpError(
        error.response?.data?.error ||
          error.message ||
          "Erro ao conectar com o ERP",
      );
    } finally {
      setErpLoading(false);
      setErpFetching(false);
    }
  };

  // Selecionar cliente do ERP e preencher formulário
  const selectErpCliente = (cliente) => {
    setFormData({
      ...formData,
      cnpj: cliente.cnpj,
      nome_empresa: cliente.nome_fantasia || cliente.razao_social,
      numero_celular: cliente.telefone.replace(/\D/g, "") || "",
    });
    setShowErpDialog(false);
    setErpClientes([]);
    setErpSearch("");
  };

  // Abrir modal de busca ERP
  const openErpSearch = () => {
    setShowErpDialog(true);
    setErpClientes([]);
    setErpSearch("");
    setErpError("");
    fetchErpClientes();
  };

  // Buscar no ERP com debounce
  const handleErpSearch = (e) => {
    e.preventDefault();
    fetchErpClientes(erpSearch);
  };

  const openEditDialog = (empresa) => {
    setSelectedEmpresa(empresa);
    setFormData({
      cnpj: empresa.cnpj,
      nome_empresa: empresa.nome_empresa,
      numero_celular: empresa.numero_celular || "",
      status: empresa.status,
      consentimento_whatsapp_habilitado:
        empresa.consentimento_whatsapp_habilitado !== false,
    });
    setFormError("");
    setShowEditDialog(true);
  };

  const getStatusBadge = (status, whatsappStatus) => {
    const isConnected = whatsappStatus === "CONECTADO";

    return (
      <div className="flex gap-2">
        <Badge
          className={
            status === "ativo"
              ? "bg-blue-100 text-blue-700"
              : "bg-red-100 text-red-700"
          }
        >
          {status}
        </Badge>
        <Badge
          className={
            isConnected
              ? "bg-green-100 text-green-700"
              : "bg-slate-100 text-slate-600"
          }
        >
          {isConnected ? (
            <Wifi className="w-3 h-3 mr-1" />
          ) : (
            <WifiOff className="w-3 h-3 mr-1" />
          )}
          {whatsappStatus || "DESCONECTADO"}
        </Badge>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-100" data-testid="admin-empresas">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/admin/dashboard")}
                className="text-slate-500 hover:text-blue-600"
                data-testid="back-to-dashboard"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Voltar
              </Button>
              <div className="h-6 w-px bg-slate-300" />
              <div className="flex items-center gap-3">
                <Building2 className="w-5 h-5 text-blue-600" />
                <h1 className="text-sm sm:text-lg font-semibold text-slate-800">
                  Gerenciar Empresas
                </h1>
              </div>
            </div>

            <Button
              onClick={() => {
                setFormData({
                  cnpj: "",
                  nome_empresa: "",
                  numero_celular: "",
                  status: "ativo",
                  consentimento_whatsapp_habilitado: true,
                });
                setFormError("");
                setShowCreateDialog(true);
              }}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="create-empresa-button"
            >
              <Plus className="w-4 h-4 sm:mr-2" />
              <p className="hidden sm:flex">Nova Empresa</p>
            </Button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <Card className="bg-white border-slate-200 mb-6 shadow-sm">
          <CardContent className="pt-6">
            <form
              onSubmit={handleSearch}
              className="flex flex-col md:flex-row gap-4"
            >
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Buscar por CNPJ ou nome..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-10 bg-white border-slate-300"
                    data-testid="search-input"
                  />
                </div>
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger
                  className="w-full md:w-40 bg-white border-slate-300"
                  data-testid="status-filter"
                >
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-200">
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="ativo">Ativo</SelectItem>
                  <SelectItem value="inativo">Inativo</SelectItem>
                  <SelectItem value="bloqueado">Bloqueado</SelectItem>
                </SelectContent>
              </Select>
              <Button
                type="submit"
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="search-button"
              >
                <Search className="w-4 h-4 mr-2" />
                Buscar
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => fetchEmpresas(true)}
                disabled={refreshing}
                className="border-slate-300"
                data-testid="refresh-button"
              >
                <RefreshCw
                  className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`}
                />
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Table */}
        <Card className="bg-white border-slate-200 shadow-sm">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
              </div>
            ) : empresas.length === 0 ? (
              <div className="text-center py-12">
                <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">Nenhuma empresa encontrada</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="empresas-table">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50">
                      <th className="text-left p-4 text-slate-600 font-medium">
                        CNPJ
                      </th>
                      <th className="text-left p-4 text-slate-600 font-medium">
                        Nome
                      </th>
                      <th className="text-left p-4 text-slate-600 font-medium">
                        Status
                      </th>
                      <th className="text-right p-4 text-slate-600 font-medium">
                        Ações
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {empresas.map((empresa) => (
                      <tr
                        key={empresa._id}
                        className="border-b border-slate-100 hover:bg-slate-50"
                        data-testid={`empresa-row-${empresa.cnpj}`}
                      >
                        <td className="p-4 text-slate-800 font-mono">
                          {empresa.cnpj}
                        </td>
                        <td className="p-4 text-slate-800">
                          {empresa.nome_empresa}
                        </td>
                        <td className="p-4">
                          {getStatusBadge(
                            empresa.status,
                            empresa.whatsapp_status,
                          )}
                        </td>
                        <td className="p-4">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => fetchEmpresaDetail(empresa.cnpj)}
                              className="text-slate-500 hover:text-blue-600"
                              data-testid={`view-${empresa.cnpj}`}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditDialog(empresa)}
                              className="text-slate-500 hover:text-blue-600"
                              data-testid={`edit-${empresa.cnpj}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSelectedEmpresa(empresa);
                                setShowResetDialog(true);
                              }}
                              className="text-amber-500 hover:text-amber-600"
                              data-testid={`reset-${empresa.cnpj}`}
                            >
                              <RotateCcw className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSelectedEmpresa(empresa);
                                setShowDeleteDialog(true);
                              }}
                              className="text-red-500 hover:text-red-600"
                              data-testid={`delete-${empresa.cnpj}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="flex items-center justify-between p-4 border-t border-slate-200">
                <p className="text-sm text-slate-500">
                  Mostrando {empresas.length} de {pagination.total} empresas
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setPagination((prev) => ({
                        ...prev,
                        page: prev.page - 1,
                      }))
                    }
                    disabled={pagination.page === 1}
                    className="border-slate-300"
                    data-testid="prev-page"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-sm text-slate-500">
                    Página {pagination.page} de {pagination.totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setPagination((prev) => ({
                        ...prev,
                        page: prev.page + 1,
                      }))
                    }
                    disabled={pagination.page === pagination.totalPages}
                    className="border-slate-300"
                    data-testid="next-page"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent
          className="bg-white border-slate-200 w-[95vw] max-w-lg rounded-2xl max-h-[90vh] overflow-y-auto"
          data-testid="create-dialog"
        >
          <DialogHeader>
            <DialogTitle className="text-slate-800">Nova Empresa</DialogTitle>
            <DialogDescription className="text-slate-500">
              Preencha os dados para cadastrar uma nova empresa.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate}>
            <div className="space-y-4 py-4">
              {formError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {formError}
                </div>
              )}
              <div className="space-y-2">
                <Label className="text-slate-700">CNPJ</Label>
                <div className="flex gap-2">
                  <Input
                    value={formData.cnpj}
                    onChange={(e) =>
                      setFormData({ ...formData, cnpj: e.target.value })
                    }
                    placeholder="00000000000000"
                    className="bg-white border-slate-300 flex-1"
                    required
                    data-testid="input-cnpj"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={openErpSearch}
                    className="border-blue-300 text-blue-600 hover:bg-blue-50 px-3 sm:px-4"
                    title="Buscar no ERP Wstech"
                    data-testid="btn-search-erp"
                  >
                    <Database className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-xs text-slate-500">
                  Clique no botão para buscar clientes no ERP
                </p>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-700">Nome da Empresa</Label>
                <Input
                  value={formData.nome_empresa}
                  onChange={(e) =>
                    setFormData({ ...formData, nome_empresa: e.target.value })
                  }
                  placeholder="Nome da empresa"
                  className="bg-white border-slate-300"
                  required
                  data-testid="input-nome"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-700">Número do Celular</Label>
                <Input
                  value={formData.numero_celular}
                  onChange={(e) =>
                    setFormData({ ...formData, numero_celular: e.target.value })
                  }
                  placeholder="5511999999999"
                  className="bg-white border-slate-300"
                  required
                  data-testid="input-celular"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-700">Status</Label>
                <Select
                  value={formData.status}
                  onValueChange={(v) => setFormData({ ...formData, status: v })}
                >
                  <SelectTrigger
                    className="bg-white border-slate-300"
                    data-testid="input-status"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-slate-200">
                    <SelectItem value="ativo">Ativo</SelectItem>
                    <SelectItem value="inativo">Inativo</SelectItem>
                    <SelectItem value="bloqueado">Bloqueado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-0 mt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowCreateDialog(false)}
                className="border-slate-300 w-full sm:w-auto"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                className="bg-blue-600 hover:bg-blue-700 w-full sm:w-auto"
                disabled={formLoading}
                data-testid="submit-create"
              >
                {formLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : null}
                Criar Empresa
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent
          className="bg-white border-slate-200 w-[95vw] max-w-lg rounded-2xl max-h-[90vh] overflow-y-auto"
          data-testid="edit-dialog"
        >
          <DialogHeader>
            <DialogTitle className="text-slate-800">Editar Empresa</DialogTitle>
            <DialogDescription className="text-slate-500 break-all">
              CNPJ: {selectedEmpresa?.cnpj}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdate}>
            <div className="space-y-4 py-4">
              {formError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {formError}
                </div>
              )}
              <div className="space-y-2">
                <Label className="text-slate-700">Nome da Empresa</Label>
                <Input
                  value={formData.nome_empresa}
                  onChange={(e) =>
                    setFormData({ ...formData, nome_empresa: e.target.value })
                  }
                  className="bg-white border-slate-300"
                  required
                  data-testid="edit-nome"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-700">Número do Celular</Label>
                <Input
                  value={formData.numero_celular}
                  onChange={(e) =>
                    setFormData({ ...formData, numero_celular: e.target.value })
                  }
                  className="bg-white border-slate-300"
                  required
                  data-testid="edit-celular"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-700">Status</Label>
                <Select
                  value={formData.status}
                  onValueChange={(v) => setFormData({ ...formData, status: v })}
                >
                  <SelectTrigger
                    className="bg-white border-slate-300"
                    data-testid="edit-status"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-slate-200">
                    <SelectItem value="ativo">Ativo</SelectItem>
                    <SelectItem value="inativo">Inativo</SelectItem>
                    <SelectItem value="bloqueado">Bloqueado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-start justify-between gap-4 border-t border-slate-200 pt-4">
                <div className="space-y-1">
                  <Label
                    htmlFor="edit-whatsapp-consent"
                    className="text-slate-700"
                  >
                    Exigir autorização para mensagens
                  </Label>
                  <p className="text-sm text-slate-500">
                    Quando desativada, os envios seguem normalmente. As
                    autorizações existentes permanecem guardadas.
                  </p>
                </div>
                <Switch
                  id="edit-whatsapp-consent"
                  checked={formData.consentimento_whatsapp_habilitado}
                  onCheckedChange={(checked) =>
                    setFormData({
                      ...formData,
                      consentimento_whatsapp_habilitado: checked,
                    })
                  }
                  aria-label="Exigir autorização para mensagens"
                  data-testid="edit-whatsapp-consent"
                />
              </div>
            </div>
            <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-0 mt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowEditDialog(false)}
                className="border-slate-300 w-full sm:w-auto"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                className="bg-blue-600 hover:bg-blue-700 w-full sm:w-auto"
                disabled={formLoading}
                data-testid="submit-edit"
              >
                {formLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : null}
                Salvar Alterações
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent
          className="bg-white border-slate-200 w-[95vw] max-w-2xl rounded-2xl max-h-[90vh] overflow-y-auto"
          data-testid="detail-dialog"
        >
          <DialogHeader>
            <DialogTitle className="text-slate-800">
              Detalhes da Empresa
            </DialogTitle>
          </DialogHeader>
          {empresaDetail && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-500">CNPJ</Label>
                  <p className="text-slate-800 font-mono break-all">
                    {empresaDetail.empresa.cnpj}
                  </p>
                </div>
                <div>
                  <Label className="text-slate-500">Status</Label>
                  <p>
                    <Badge
                      className={
                        empresaDetail.empresa.status === "ativo"
                          ? "bg-blue-100 text-blue-700"
                          : "bg-red-100 text-red-700"
                      }
                    >
                      {empresaDetail.empresa.status}
                    </Badge>
                  </p>
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-slate-500">Nome</Label>
                  <p className="text-slate-800 break-words">
                    {empresaDetail.empresa.nome_empresa}
                  </p>
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-slate-500">Celular</Label>
                  <p className="text-slate-800">
                    {empresaDetail.empresa.numero_celular}
                  </p>
                </div>
              </div>

              <div className="pt-4 border-t border-slate-200">
                <Label className="text-slate-500">API Key</Label>
                <div className="flex items-center gap-2 mt-1">
                  <code className="flex-1 p-2 bg-slate-100 rounded text-xs text-slate-700 overflow-x-auto whitespace-nowrap">
                    {empresaDetail.empresa.api_key}
                  </code>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        copyToClipboard(empresaDetail.empresa.api_key)
                      }
                      className="text-slate-500 px-2 sm:px-3"
                    >
                      {copiedKey ? (
                        <Check className="w-4 h-4 text-green-600" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        handleRegenerateKey(empresaDetail.empresa.cnpj)
                      }
                      className="text-amber-500 px-2 sm:px-3"
                    >
                      <Key className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t border-slate-200">
                <Label className="text-slate-500">WhatsApp</Label>
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 mt-1">
                  <Badge
                    className={`w-fit ${
                      empresaDetail.whatsapp.status === "CONECTADO"
                        ? "bg-green-100 text-green-700"
                        : "bg-slate-100 text-slate-600"
                    }`}
                  >
                    {empresaDetail.whatsapp.status === "CONECTADO" ? (
                      <Wifi className="w-3 h-3 mr-1" />
                    ) : (
                      <WifiOff className="w-3 h-3 mr-1" />
                    )}
                    {empresaDetail.whatsapp.status}
                  </Badge>
                  {empresaDetail.whatsapp.ultima_conexao && (
                    <span className="text-xs text-slate-500">
                      Última conexão:{" "}
                      {new Date(
                        empresaDetail.whatsapp.ultima_conexao,
                      ).toLocaleString("pt-BR")}
                    </span>
                  )}
                </div>
              </div>

              <div className="pt-4 border-t border-slate-200 grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-2">
                <div className="text-center bg-slate-50 p-3 rounded-lg sm:bg-transparent sm:p-0">
                  <p className="text-2xl font-bold text-blue-600">
                    {empresaDetail.estatisticas.mensagens_enviadas}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">Enviadas</p>
                </div>
                <div className="text-center bg-slate-50 p-3 rounded-lg sm:bg-transparent sm:p-0">
                  <p className="text-2xl font-bold text-slate-600">
                    {empresaDetail.estatisticas.mensagens_recebidas}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">Recebidas</p>
                </div>
                <div className="text-center bg-slate-50 p-3 rounded-lg sm:bg-transparent sm:p-0">
                  <p className="text-2xl font-bold text-slate-600">
                    {empresaDetail.estatisticas.envios_massa}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">Envios em Massa</p>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDetailDialog(false)}
              className="border-slate-300 w-full sm:w-auto mt-2 sm:mt-0"
            >
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent
          className="bg-white border-slate-200 w-[95vw] rounded-2xl"
          data-testid="delete-dialog"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="text-slate-800">
              Confirmar Exclusão
            </AlertDialogTitle>
            <AlertDialogDescription className="text-slate-500">
              Tem certeza que deseja excluir a empresa{" "}
              <strong className="break-all">
                {selectedEmpresa?.nome_empresa}
              </strong>
              ?
              <br />
              <br />
              Esta ação irá remover permanentemente todos os dados da empresa,
              incluindo mensagens, sessões e logs.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2 sm:gap-0 mt-4">
            <AlertDialogCancel className="bg-white border-slate-300 text-slate-700 hover:bg-slate-50 w-full sm:w-auto m-0">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700 text-white w-full sm:w-auto m-0"
              disabled={formLoading}
              data-testid="confirm-delete"
            >
              {formLoading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : null}
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reset Session Confirmation */}
      <AlertDialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <AlertDialogContent
          className="bg-white border-slate-200 w-[95vw] rounded-2xl"
          data-testid="reset-dialog"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="text-slate-800">
              Resetar Sessão WhatsApp
            </AlertDialogTitle>
            <AlertDialogDescription className="text-slate-500">
              Tem certeza que deseja resetar a sessão WhatsApp da empresa{" "}
              <strong className="break-all">
                {selectedEmpresa?.nome_empresa}
              </strong>
              ?
              <br />
              <br />A empresa precisará escanear um novo QR Code para
              reconectar.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2 sm:gap-0 mt-4">
            <AlertDialogCancel className="bg-white border-slate-300 text-slate-700 hover:bg-slate-50 w-full sm:w-auto m-0">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleResetSession}
              className="bg-amber-600 hover:bg-amber-700 text-white w-full sm:w-auto m-0"
              disabled={formLoading}
              data-testid="confirm-reset"
            >
              {formLoading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : null}
              Resetar Sessão
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Modal Busca ERP Wstech */}
      <Dialog open={showErpDialog} onOpenChange={setShowErpDialog}>
        <DialogContent
          className="bg-white border-slate-200 w-[95vw] max-w-5xl max-h-[90vh] rounded-2xl flex flex-col"
          data-testid="erp-dialog"
        >
          <DialogHeader className="shrink-0">
            <DialogTitle className="text-slate-800 flex items-center gap-2">
              <Database className="w-5 h-5 text-blue-600" />
              Buscar Cliente no ERP Wstech
            </DialogTitle>
            <DialogDescription className="text-slate-500">
              Selecione um cliente para preencher automaticamente os dados da
              empresa.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 flex-1 flex flex-col min-h-0">
            {/* Busca */}
            <form
              onSubmit={handleErpSearch}
              className="flex gap-2 mb-4 shrink-0"
            >
              <Input
                value={erpSearch}
                onChange={(e) => setErpSearch(e.target.value)}
                placeholder="Buscar por código, CNPJ, razão social ou cidade..."
                className="bg-white border-slate-300 flex-1"
                data-testid="erp-search-input"
              />
              <Button
                type="submit"
                className="bg-blue-600 hover:bg-blue-700 px-3 sm:px-4"
                disabled={erpLoading}
              >
                {erpLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Search className="w-4 h-4" />
                )}
              </Button>
            </form>

            {/* Erro */}
            {erpError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm mb-4 shrink-0">
                {erpError}
              </div>
            )}

            {/* Lista de clientes com Scroll Horizontal e Vertical */}
            <div className="border border-slate-200 rounded-lg overflow-hidden flex-1 flex flex-col min-h-0 bg-white">
              {erpLoading ? (
                <div className="flex items-center justify-center py-12 flex-1">
                  <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
                </div>
              ) : erpClientes.length === 0 ? (
                <div className="text-center py-12 flex-1">
                  <Database className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">Nenhum cliente encontrado</p>
                </div>
              ) : (
                <div className="overflow-auto flex-1">
                  <table className="w-full text-sm min-w-[600px]">
                    <thead className="bg-slate-50 sticky top-0 z-10 shadow-sm">
                      <tr className="border-b border-slate-200">
                        <th className="text-left p-3 text-slate-600 font-medium whitespace-nowrap">
                          Código
                        </th>
                        <th className="text-left p-3 text-slate-600 font-medium whitespace-nowrap">
                          CNPJ
                        </th>
                        <th className="text-left p-3 text-slate-600 font-medium">
                          Razão Social / Fantasia
                        </th>
                        <th className="text-left p-3 text-slate-600 font-medium whitespace-nowrap">
                          Cidade
                        </th>
                        <th className="text-left p-3 text-slate-600 font-medium whitespace-nowrap">
                          Telefone
                        </th>
                        <th className="text-right p-3 bg-slate-50 sticky right-0"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {erpClientes.map((cliente) => (
                        <tr
                          key={cliente.id}
                          className="border-b border-slate-100 hover:bg-blue-50 cursor-pointer transition-colors"
                          onClick={() => selectErpCliente(cliente)}
                          data-testid={`erp-cliente-${cliente.id}`}
                        >
                          <td className="p-3 text-slate-600 font-mono whitespace-nowrap">
                            {cliente.id}
                          </td>
                          <td className="p-3 text-slate-800 font-mono text-xs whitespace-nowrap">
                            {cliente.cnpj}
                          </td>
                          <td className="p-3 min-w-[200px]">
                            <div
                              className="text-slate-800 font-medium truncate max-w-[250px]"
                              title={cliente.razao_social}
                            >
                              {cliente.nome_fantasia || cliente.razao_social}
                            </div>
                            {cliente.nome_fantasia && (
                              <div
                                className="text-xs text-slate-500 truncate max-w-[250px]"
                                title={cliente.razao_social}
                              >
                                {cliente.razao_social}
                              </div>
                            )}
                          </td>
                          <td className="p-3 whitespace-nowrap">
                            <div className="flex items-center gap-1 text-slate-600">
                              <MapPin className="w-3 h-3 shrink-0" />
                              <span className="truncate max-w-[120px]">
                                {cliente.cidade}
                                {cliente.uf ? `/${cliente.uf}` : ""}
                              </span>
                            </div>
                          </td>
                          <td className="p-3 whitespace-nowrap">
                            {cliente.telefone && (
                              <div className="flex items-center gap-1 text-slate-600">
                                <Phone className="w-3 h-3 shrink-0" />
                                {cliente.telefone}
                              </div>
                            )}
                          </td>
                          <td className="p-3 text-right bg-white group-hover:bg-blue-50 sticky right-0 transition-colors">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-100 whitespace-nowrap"
                              onClick={(e) => {
                                e.stopPropagation();
                                selectErpCliente(cliente);
                              }}
                            >
                              Selecionar
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="shrink-0 mt-2">
            <Button
              variant="outline"
              onClick={() => setShowErpDialog(false)}
              className="border-slate-300 w-full sm:w-auto"
            >
              Cancelar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
